import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer,
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
} from "recharts";
import { Star, ThumbsUp, PenLine, MapPin, Building2, ArrowLeft, ShieldCheck, Flag, BadgeCheck, Reply, TrendingUp, Pencil, Trash2, Lock, RefreshCw, Share2, Scale } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { TRAITS, scoreColor, scoreLabel } from "@/lib/traits";
import { useAuth } from "@/context/AuthContext";
import FavoriteButton from "@/components/FavoriteButton";
import CheckinModal from "@/components/CheckinModal";
import TrajectoryStrip from "@/components/TrajectoryStrip";
import SupervisorStatCard from "@/components/share/SupervisorStatCard";
import { toast } from "sonner";

function initials(name) {
  return name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
}

const REPORT_REASONS = [
  "Reveals the reviewer's or someone's identity",
  "Makes a specific safety, legal, or misconduct allegation",
  "Dishonest or fabricated",
  "Harassment or hate speech",
  "Not about this supervisor",
  "Spam or advertising",
  "Other",
];

export default function SupervisorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, setAuthOpen } = useAuth();
  const [sup, setSup] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewSort, setReviewSort] = useState("recent");
  const [reportFor, setReportFor] = useState(null);
  const [reportReason, setReportReason] = useState("");
  const [reportDetails, setReportDetails] = useState("");
  const [reporting, setReporting] = useState(false);
  const [trends, setTrends] = useState([]);
  const [respondFor, setRespondFor] = useState(null);
  const [responseText, setResponseText] = useState("");
  const [responding, setResponding] = useState(false);
  const [reviewsMeta, setReviewsMeta] = useState({ total: 0, has_more: false, hidden: false, min_reviews: 5 });
  const [reviewSkip, setReviewSkip] = useState(0);
  const [trajectory, setTrajectory] = useState(null);
  const [checkinFor, setCheckinFor] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);

  const loadTrajectory = useCallback(() => {
    api.get(`/supervisors/${id}/trajectory`).then((r) => setTrajectory(r.data)).catch(() => {});
  }, [id]);

  const loadSup = useCallback(async () => {
    const s = await api.get(`/supervisors/${id}`);
    setSup(s.data);
  }, [id]);

  const REVIEW_PAGE = 10;
  const loadReviews = useCallback(async (skip = 0) => {
    const r = await api.get(`/supervisors/${id}/reviews`, { params: { sort: reviewSort, limit: REVIEW_PAGE, skip } });
    setReviews((prev) => (skip === 0 ? r.data.items : [...prev, ...r.data.items]));
    setReviewsMeta({ total: r.data.total, has_more: r.data.has_more, hidden: !!r.data.hidden, min_reviews: r.data.min_reviews || 5 });
    setReviewSkip(skip + r.data.items.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, reviewSort, user?.id]);

  useEffect(() => { loadSup(); }, [loadSup]);
  useEffect(() => { loadReviews(); }, [loadReviews]);
  useEffect(() => { api.get(`/supervisors/${id}/trends`).then((r) => setTrends(r.data)); }, [id]);
  useEffect(() => { loadTrajectory(); }, [loadTrajectory]);

  const onCheckinDone = () => {
    loadReviews();
    loadTrajectory();
    api.get(`/supervisors/${id}/trends`).then((r) => setTrends(r.data));
  };

  const isManager = user && sup && (sup.owner_id === user.id || user.role === "admin");
  const isAdmin = user?.role === "admin";
  const canClaim = user && sup && !sup.owner_id;

  const claim = async () => {
    if (!user) { setAuthOpen(true); return; }
    try {
      await api.post(`/supervisors/${id}/claim`);
      toast.success("Profile claimed — you can now respond to reviews");
      loadSup();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not claim");
    }
  };

  const openRespond = (r) => {
    setRespondFor(r.id);
    setResponseText(r.response?.text || "");
  };

  const submitResponse = async () => {
    if (!responseText.trim()) return;
    setResponding(true);
    try {
      await api.post(`/reviews/${respondFor}/response`, { text: responseText });
      toast.success("Response posted");
      setRespondFor(null);
      loadReviews();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not post response");
    } finally {
      setResponding(false);
    }
  };

  const deleteResponse = async (rid) => {
    await api.delete(`/reviews/${rid}/response`);
    toast("Response removed");
    loadReviews();
  };

  const withdrawReview = async (rid) => {
    await api.delete(`/reviews/${rid}`);
    toast.success("Your review has been withdrawn");
    loadSup();
    loadReviews();
    api.get(`/supervisors/${id}/trends`).then((r) => setTrends(r.data));
  };

  const goWrite = () => {
    if (!user) {
      setAuthOpen(true);
      toast.info("Sign in to write a review");
      return;
    }
    navigate(`/supervisor/${id}/review`);
  };

  const upvote = async (rid) => {
    if (!user) {
      setAuthOpen(true);
      toast.info("Sign in to mark reviews helpful");
      return;
    }
    const { data } = await api.post(`/reviews/${rid}/helpful`);
    setReviews((rs) => rs.map((r) => (r.id === rid ? { ...r, helpful: data.helpful } : r)));
  };

  const openReport = (rid) => {
    if (!user) {
      setAuthOpen(true);
      toast.info("Sign in to report a review");
      return;
    }
    setReportFor(rid);
    setReportReason("");
    setReportDetails("");
  };

  const submitReport = async () => {
    if (!reportReason) return;
    setReporting(true);
    try {
      const { data } = await api.post(`/reviews/${reportFor}/report`, { reason: reportReason, details: reportDetails });
      toast.success(data.message || "Review reported");
      setReportFor(null);
    } catch (e) {
      toast.error("Could not submit report");
    } finally {
      setReporting(false);
    }
  };

  if (!sup) return <div className="max-w-7xl mx-auto px-8 py-20 text-slate-400 font-mono">Loading...</div>;

  const radarData = TRAITS.map((t) => ({ trait: t.short, score: sup.traits?.[t.key] || 0 }));

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
      <button onClick={() => navigate("/supervisors")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-emerald-700 mb-6">
        <ArrowLeft size={16} /> Back to directory
      </button>

      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 lb-hero-mesh opacity-70" />
        <div className="relative flex flex-col sm:flex-row gap-6 items-start justify-between">
          <div className="flex gap-5 items-start">
            <div className="h-20 w-20 rounded-xl bg-slate-900 text-white font-display flex items-center justify-center text-3xl shrink-0" style={{ fontWeight: 800 }}>
              {initials(sup.name)}
            </div>
            <div>
              <h1 data-testid="supervisor-detail-name" className="font-display text-4xl sm:text-5xl text-slate-900 leading-none" style={{ fontWeight: 900 }}>
                {sup.name}
              </h1>
              <p className="text-lg text-slate-600 mt-1">{sup.title}</p>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-sm text-slate-500">
                <span className="flex items-center gap-1.5">
                  <Building2 size={15} />
                  <button
                    data-testid="detail-company-link"
                    onClick={() => navigate(`/company/${encodeURIComponent(sup.company)}`)}
                    className="hover:text-emerald-700 hover:underline"
                  >
                    {sup.company}
                  </button>
                </span>
                <Badge variant="secondary" className="bg-slate-100 text-slate-600">{sup.department}</Badge>
                {sup.location && <span className="flex items-center gap-1.5"><MapPin size={15} /> {sup.location}</span>}
                {sup.claimed && <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-medium"><BadgeCheck size={14} /> Claimed profile</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <FavoriteButton kind="supervisor" refId={sup.id} />
            {!sup.ratings_hidden && (
              <Button data-testid="share-card-btn" onClick={() => setShareOpen(true)} variant="outline" className="rounded-full border-slate-300 gap-1.5">
                <Share2 size={16} /> Share card
              </Button>
            )}
            {canClaim && (
              <Button data-testid="claim-profile-btn" onClick={claim} variant="outline" className="rounded-full border-slate-300 gap-1.5">
                <BadgeCheck size={16} /> Claim
              </Button>
            )}
            <Button data-testid="write-review-btn" onClick={goWrite} className="bg-slate-900 hover:bg-emerald-600 rounded-full px-6 gap-2">
              <PenLine size={16} /> Write a Review
            </Button>
          </div>
        </div>
      </div>

      {/* Legal hold notice */}
      {sup.legal_hold && !isAdmin && (
        <div data-testid="legal-hold-notice" className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mt-6 flex items-start gap-3">
          <Scale size={20} className="text-amber-700 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-900">Some content is temporarily unavailable pending review</p>
            <p className="text-sm text-amber-800 mt-1 leading-relaxed">One or more reviews of this profile have been placed on hold while we review a request under our published guidelines. Reviewer identities are never shared. Content is restored automatically if the review finds no violation.</p>
          </div>
        </div>
      )}

      {/* Anonymity lock (sub-threshold) */}
      {sup.ratings_hidden && !(sup.legal_hold && !isAdmin) && (
        <div data-testid="ratings-locked-panel" className="bg-slate-900 rounded-2xl p-7 sm:p-8 text-white mt-6 lb-grain">
          <div className="flex flex-col sm:flex-row gap-5 items-start">
            <div className="h-14 w-14 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center shrink-0">
              <Lock size={24} className="text-emerald-400" />
            </div>
            <div className="flex-1">
              <p className="font-mono text-xs uppercase tracking-widest text-emerald-400 mb-1">Anonymity protection</p>
              <h2 className="font-display text-3xl leading-tight" style={{ fontWeight: 800 }}>
                Ratings unlock at {sup.min_reviews} reviews
              </h2>
              <p className="text-slate-300 mt-3 leading-relaxed max-w-2xl">
                {isAdmin
                  ? "Admin view: this supervisor is below the public threshold, so employees see nothing here yet. You can see the real data below for moderation."
                  : `On small teams, a single rating can be traced back to the person who wrote it. Scores, traits, and reviews for ${sup.name} stay hidden until at least ${sup.min_reviews} employees have shared their experience — so no one can be singled out.`}
              </p>
              {!isAdmin && (
                <Button data-testid="locked-write-review-btn" onClick={goWrite} className="mt-5 bg-emerald-500 hover:bg-emerald-400 text-slate-900 rounded-full px-6 gap-2 font-semibold">
                  <PenLine size={16} /> Be one of the first to review
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Score + Radar */}
      {(!sup.ratings_hidden || sup.review_count > 0) && (
      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <div className="bg-slate-900 rounded-2xl p-7 text-white flex flex-col justify-center lb-grain">
          <p className="font-mono text-xs uppercase tracking-widest text-emerald-400 mb-2">Overall leadership score</p>
          <div className="flex items-end gap-2">
            <span data-testid="supervisor-overall-score" className="font-display text-7xl leading-none" style={{ fontWeight: 900 }}>
              {sup.overall > 0 ? sup.overall.toFixed(1) : "—"}
            </span>
            <span className="text-slate-400 mb-2 text-xl">/ 5</span>
          </div>
          <div className="flex items-center gap-1 mt-3">
            {[1, 2, 3, 4, 5].map((n) => (
              <Star key={n} size={18} fill={n <= Math.round(sup.overall) ? "#F59E0B" : "transparent"} color={n <= Math.round(sup.overall) ? "#F59E0B" : "#475569"} />
            ))}
          </div>
          <p className="mt-3 text-emerald-300 font-semibold">{scoreLabel(sup.overall)}</p>
          <div className="mt-5 pt-5 border-t border-slate-700 flex gap-6 text-sm">
            <div>
              <div className="font-display text-2xl" style={{ fontWeight: 700 }}>{sup.review_count}</div>
              <div className="text-slate-400">Reviews</div>
            </div>
            <div>
              <div className="font-display text-2xl" style={{ fontWeight: 700 }}>{sup.recommend_rate}%</div>
              <div className="text-slate-400">Recommend</div>
            </div>
          </div>
          {sup.top_tags?.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-2" data-testid="supervisor-top-tags">
              {sup.top_tags.map((t) => <span key={t.tag} className="text-[11px] font-bold px-3 py-1 rounded-full bg-rose-500 text-white">{t.tag} ×{t.count}</span>)}
            </div>
          )}
        </div>

        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-6">
          <p className="font-mono text-xs uppercase tracking-widest text-emerald-600 mb-4">8-Trait breakdown</p>
          {sup.review_count === 0 ? (
            <div className="text-center py-14 text-slate-400">No trait data yet. Be the first to review.</div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6 items-center">
              <div style={{ height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData} outerRadius="72%">
                    <PolarGrid stroke="#E2E8F0" />
                    <PolarAngleAxis dataKey="trait" tick={{ fontSize: 10, fill: "#64748B" }} />
                    <PolarRadiusAxis domain={[0, 5]} tick={false} axisLine={false} />
                    <Radar dataKey="score" stroke="#059669" fill="#059669" fillOpacity={0.35} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2.5">
                {TRAITS.map((t) => {
                  const sc = sup.traits?.[t.key] || 0;
                  return (
                    <div key={t.key}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="flex items-center gap-1.5 text-slate-600"><t.icon size={13} className="text-emerald-600" /> {t.short}</span>
                        <span className="font-mono font-semibold" style={{ color: scoreColor(sc) }}>{sc.toFixed(1)}</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${(sc / 5) * 100}%`, backgroundColor: scoreColor(sc) }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Trajectory from reviewer check-ins */}
      {!(sup.legal_hold && !isAdmin) && <TrajectoryStrip data={trajectory} />}

      {/* Trait Trends */}
      {trends.length >= 2 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 mt-6">
          <p className="font-mono text-xs uppercase tracking-widest text-emerald-600 mb-1 flex items-center gap-1.5">
            <TrendingUp size={13} /> Rating trends over time
          </p>
          <p className="text-sm text-slate-500 mb-4">How this supervisor's overall rating has shifted quarter over quarter.</p>
          <div style={{ height: 280 }} data-testid="trends-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trends} margin={{ top: 5, right: 20, bottom: 5, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="period" tick={{ fontSize: 12, fill: "#64748B" }} />
                <YAxis domain={[0, 5]} tick={{ fontSize: 12, fill: "#64748B" }} />
                <RTooltip />
                <Line type="monotone" dataKey="overall" name="Overall" stroke="#059669" strokeWidth={3} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : sup.review_count > 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-5 mt-6 flex items-center gap-3 text-slate-500" data-testid="trends-placeholder">
          <TrendingUp size={18} className="text-emerald-600" />
          <p className="text-sm">Rating trends will appear here once several quarters each have enough reviews to chart anonymously.</p>
        </div>
      ) : null}

      {/* Reviews */}
      <div className="mt-10">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <h2 className="font-display text-3xl text-slate-900" style={{ fontWeight: 800 }}>
            Anonymous Reviews {!(reviewsMeta.hidden && !isAdmin) && <span className="text-slate-400 text-2xl">({reviewsMeta.total})</span>}
          </h2>
          {reviews.length > 1 && (
            <Select value={reviewSort} onValueChange={setReviewSort}>
              <SelectTrigger data-testid="review-sort-select" className="w-52 h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="recent">Most recent</SelectItem>
                <SelectItem value="helpful">Most helpful</SelectItem>
                <SelectItem value="highest">Highest rated</SelectItem>
                <SelectItem value="lowest">Lowest rated</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
        {reviewsMeta.hidden && !isAdmin && !sup.legal_hold && (
          <div data-testid="reviews-hidden-notice" className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 mb-5 flex items-start gap-3">
            <Lock size={18} className="text-emerald-700 shrink-0 mt-0.5" />
            <p className="text-sm text-slate-700 leading-relaxed">
              Reviews for this supervisor are hidden until {reviewsMeta.min_reviews} employees have reviewed them.
              {reviews.length > 0 ? " Only you can see your own review below — it will be published automatically once the threshold is reached." : " Your review will be saved privately and published automatically once the threshold is reached."}
            </p>
          </div>
        )}
        {reviews.length === 0 ? (
          reviewsMeta.hidden && !isAdmin ? null : (
          <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center">
            <p className="text-slate-500 mb-4">No reviews yet. Share your honest experience.</p>
            <Button onClick={goWrite} className="bg-slate-900 hover:bg-emerald-600 rounded-full">Write the first review</Button>
          </div>
          )
        ) : (
          <div className="space-y-5" data-testid="reviews-list">
            {reviews.map((r) => (
              <div key={r.id} data-testid="review-card" className="bg-white border border-slate-200 rounded-xl p-6">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star key={n} size={15} fill={n <= r.overall ? "#F59E0B" : "transparent"} color={n <= r.overall ? "#F59E0B" : "#CBD5E1"} />
                      ))}
                      <span className="font-display text-xl text-slate-900 ml-1" style={{ fontWeight: 700 }}>
                        {r.text_hidden && !r.title ? <span className="text-slate-400 italic text-base">Ratings-only review</span> : r.title}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1.5 text-xs text-slate-500">
                      <span className="inline-flex items-center gap-1 text-emerald-700 font-medium"><ShieldCheck size={12} /> Verified Employee</span>
                      {r.period && <><span>•</span><span data-testid="review-period" className="font-mono">{r.period}</span></>}
                      {r.reviewer_role && <><span>•</span><span>{r.reviewer_role}</span></>}
                      {r.reviewer_location && <><span>•</span><span>{r.reviewer_location}</span></>}
                      {r.tenure && <><span>•</span><span>{r.tenure}</span></>}
                      {r.edited && <><span>•</span><span className="italic">edited</span></>}
                    </div>
                  </div>
                  {r.is_mine && (
                    <div className="flex items-center gap-2 flex-wrap" data-testid={`my-review-actions-${r.id}`}>
                      <span className="text-xs font-mono uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1">Your review</span>
                      {r.checkin_available ? (
                        <Button data-testid={`checkin-review-${r.id}`} onClick={() => setCheckinFor(r)} size="sm" className="h-8 gap-1 rounded-full bg-amber-500 hover:bg-amber-600 text-white">
                          <RefreshCw size={13} /> Still accurate?
                        </Button>
                      ) : r.checkin_done_this_quarter ? (
                        <span data-testid={`checkin-done-${r.id}`} className="text-xs text-slate-500 flex items-center gap-1"><RefreshCw size={12} /> Checked in: <span className="capitalize">{r.last_checkin?.direction}</span></span>
                      ) : r.checkin_unlock_days > 0 ? (
                        <span data-testid={`checkin-locked-${r.id}`} className="text-xs text-slate-400 flex items-center gap-1" title="Check-ins unlock 90 days after your review"><RefreshCw size={12} /> Check-in in {r.checkin_unlock_days}d</span>
                      ) : null}
                      <Button data-testid={`edit-review-${r.id}`} onClick={() => navigate(`/supervisor/${id}/review?edit=${r.id}`)} variant="outline" size="sm" className="h-8 gap-1 rounded-full">
                        <Pencil size={13} /> Edit
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button data-testid={`withdraw-review-${r.id}`} variant="ghost" size="sm" className="h-8 gap-1 rounded-full text-rose-600 hover:text-rose-700 hover:bg-rose-50">
                            <Trash2 size={13} /> Withdraw
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Withdraw your review?</AlertDialogTitle>
                            <AlertDialogDescription>This permanently deletes your review of {sup.name}. You can always write a new one later.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Keep it</AlertDialogCancel>
                            <AlertDialogAction data-testid={`confirm-withdraw-${r.id}`} onClick={() => withdrawReview(r.id)} className="bg-rose-600 hover:bg-rose-700">Withdraw</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  )}
                </div>

                {r.text_hidden && (
                  <p data-testid="review-text-hidden-note" className="mt-4 text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 inline-flex items-center gap-2">
                    <Lock size={13} className="text-emerald-600" />
                    {r.is_mine ? "You chose to keep your written comments private — others only see your ratings." : "This reviewer chose to share ratings only, keeping written comments private."}
                  </p>
                )}
                {r.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3" data-testid="review-tags">
                    {r.tags.map((t) => <span key={t} className="text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-rose-500 text-white">{t}</span>)}
                  </div>
                )}
                {r.comment && <p className="text-slate-700 mt-4 leading-relaxed">{r.comment}</p>}

                <div className="grid sm:grid-cols-2 gap-4 mt-4">
                  {r.pros && (
                    <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
                      <p className="text-xs font-mono uppercase tracking-wider text-emerald-700 mb-1">Pros</p>
                      <p className="text-sm text-slate-700">{r.pros}</p>
                    </div>
                  )}
                  {r.cons && (
                    <div className="bg-rose-50 border border-rose-100 rounded-lg p-3">
                      <p className="text-xs font-mono uppercase tracking-wider text-rose-600 mb-1">Cons</p>
                      <p className="text-sm text-slate-700">{r.cons}</p>
                    </div>
                  )}
                </div>

                {(r.leadership_improvement || r.management_improvement) && (
                  <div className="grid sm:grid-cols-2 gap-4 mt-4">
                    {r.leadership_improvement && (
                      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                        <p className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-1">💡 Could improve as a leader</p>
                        <p className="text-sm text-slate-700">{r.leadership_improvement}</p>
                      </div>
                    )}
                    {r.management_improvement && (
                      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                        <p className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-1">💡 Could improve at management</p>
                        <p className="text-sm text-slate-700">{r.management_improvement}</p>
                      </div>
                    )}
                  </div>
                )}

                {r.advice && (
                  <p className="text-sm text-slate-500 mt-3 italic">Advice to management: {r.advice}</p>
                )}

                <div className="flex flex-wrap gap-1.5 mt-4">
                  {TRAITS.map((t) => (
                    <span key={t.key} className="text-xs px-2 py-0.5 rounded bg-slate-50 border border-slate-200 text-slate-500 font-mono">
                      {t.short} {r.traits?.[t.key] ?? "—"}
                    </span>
                  ))}
                </div>

                <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
                  <button
                    data-testid={`review-helpful-${r.id}`}
                    onClick={() => upvote(r.id)}
                    className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-emerald-700 transition-colors"
                  >
                    <ThumbsUp size={14} /> Helpful ({r.helpful || 0})
                  </button>
                  <button
                    data-testid={`review-report-${r.id}`}
                    onClick={() => openReport(r.id)}
                    className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-rose-600 transition-colors"
                  >
                    <Flag size={14} /> Report
                  </button>
                </div>

                {r.response && (
                  <div data-testid={`review-response-${r.id}`} className="mt-4 ml-0 sm:ml-6 bg-slate-50 border-l-4 border-emerald-500 rounded-r-lg p-4">
                    <div className="flex items-center justify-between gap-2">
                      <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700">
                        <Reply size={14} /> Response from {sup.name}
                      </span>
                      {isManager && (
                        <div className="flex gap-2">
                          <button onClick={() => openRespond(r)} className="text-xs text-slate-400 hover:text-emerald-700">Edit</button>
                          <button data-testid={`delete-response-${r.id}`} onClick={() => deleteResponse(r.id)} className="text-xs text-slate-400 hover:text-rose-600">Delete</button>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-slate-700 mt-2 leading-relaxed">{r.response.text}</p>
                  </div>
                )}

                {isManager && !r.response && (
                  <button
                    data-testid={`respond-btn-${r.id}`}
                    onClick={() => openRespond(r)}
                    className="mt-3 inline-flex items-center gap-1.5 text-sm text-emerald-700 hover:text-emerald-800 font-medium"
                  >
                    <Reply size={14} /> Respond as supervisor
                  </button>
                )}
              </div>
            ))}
            {reviewsMeta.has_more && (
              <div className="flex justify-center pt-2">
                <Button data-testid="load-more-reviews-btn" onClick={() => loadReviews(reviewSkip)} variant="outline" className="rounded-full px-8">
                  Load more reviews
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Respond (rebuttal) dialog */}
      <Dialog open={!!respondFor} onOpenChange={(o) => !o && setRespondFor(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="respond-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2" style={{ fontWeight: 800 }}>
              <Reply className="text-emerald-600" size={22} /> Respond to this review
            </DialogTitle>
          </DialogHeader>
          <DialogDescription className="text-sm text-slate-500 -mt-1">
            Your reply is public and shown beneath the review, attributed to {sup.name}.
          </DialogDescription>
          <Textarea
            data-testid="response-text-input"
            rows={5}
            value={responseText}
            onChange={(e) => setResponseText(e.target.value)}
            placeholder="Share your perspective professionally..."
          />
          <Button
            data-testid="response-submit-btn"
            onClick={submitResponse}
            disabled={responding || !responseText.trim()}
            className="w-full bg-slate-900 hover:bg-emerald-600 rounded-full"
          >
            {responding ? "Posting..." : "Post Response"}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reportFor} onOpenChange={(o) => !o && setReportFor(null)}>
        <DialogContent className="sm:max-w-md" data-testid="report-review-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2" style={{ fontWeight: 800 }}>
              <Flag className="text-rose-600" size={22} /> Report this review
            </DialogTitle>
          </DialogHeader>
          <DialogDescription className="text-sm text-slate-500 -mt-1">
            Flag reviews that break our honesty or anonymity rules. Reports are confidential.
          </DialogDescription>
          <div className="space-y-3 mt-2">
            <Select value={reportReason} onValueChange={setReportReason}>
              <SelectTrigger data-testid="report-reason-select"><SelectValue placeholder="Select a reason" /></SelectTrigger>
              <SelectContent>
                {REPORT_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
            <Textarea
              data-testid="report-details-input"
              rows={3}
              value={reportDetails}
              onChange={(e) => setReportDetails(e.target.value)}
              placeholder="Add any details (optional)"
            />
            <Button
              data-testid="report-submit-btn"
              onClick={submitReport}
              disabled={!reportReason || reporting}
              className="w-full bg-rose-600 hover:bg-rose-700 rounded-full"
            >
              {reporting ? "Submitting..." : "Submit Report"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="share-card-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl" style={{ fontWeight: 800 }}>Share this profile</DialogTitle>
            <DialogDescription>A social-ready card built from live aggregate data. Nothing on it identifies any reviewer.</DialogDescription>
          </DialogHeader>
          <SupervisorStatCard sup={sup} />
        </DialogContent>
      </Dialog>

      <CheckinModal
        review={checkinFor || {}}
        supervisorName={sup.name}
        open={!!checkinFor}
        onOpenChange={(o) => !o && setCheckinFor(null)}
        onDone={onCheckinDone}
      />
    </div>
  );
}
